/**
 * Dibuja texto (una o varias líneas, cada una con su tamaño relativo) siguiendo una
 * FORMA: recta | diagonal | arcoíris (arco arriba) | curva (arco abajo).
 *
 * El ctx ya viene trasladado al centro del print y rotado, con textAlign="center" y
 * textBaseline="middle". Como canvas no tiene textPath, cada glifo se coloca a mano
 * sobre el arco. curvature = 0..100. Se usa TANTO en el preview (PreviewSection) COMO
 * en el boceto que va a Producción (sketchRenderer), para que nunca se desincronicen.
 *
 * @param {Array<{text:string,size:number}>} lines
 * @param {number} baseFontSize  tamaño base en px (renglón size=1)
 * @param {string[]} palette      colores; [0]=relleno, [1]=contorno (bordado)
 * @param {boolean} embroidery    dibuja contorno + relleno (bordado 2 colores)
 */
export const drawShapedText = (ctx, { lines, baseFontSize, fontFamily, shape, curvature, palette, embroidery }) => {
  const shown = (lines || []).filter((l) => String(l?.text || "").length);
  if (!ctx || !shown.length) return;
  const pal = palette && palette.length ? palette : ["#222"];
  const fs = shown.map((l) => baseFontSize * (Number(l.size) > 0 ? Number(l.size) : 1));
  const gap = baseFontSize * 0.18;
  const totalH = fs.reduce((a, b) => a + b, 0) + gap * (shown.length - 1);
  const strokeW = Math.max(1, baseFontSize * 0.12);
  const curv = Number.isFinite(Number(curvature)) ? Number(curvature) : 0;
  let cur = -totalH / 2;
  let cursor = 0; // recorre la paleta por carácter (multicolor sin bordado)
  const drawChar = (ch, x, y, rot) => {
    ctx.save();
    ctx.translate(x, y);
    if (rot) ctx.rotate(rot);
    if (embroidery) {
      ctx.lineJoin = "round";
      ctx.lineWidth = strokeW;
      ctx.strokeStyle = pal[1] || "#1f2a3d";
      ctx.fillStyle = pal[0] || "#ffffff";
      ctx.strokeText(ch, 0, 0);
      ctx.fillText(ch, 0, 0);
    } else {
      ctx.fillStyle = pal[cursor % pal.length] || "#222";
      ctx.fillText(ch, 0, 0);
      cursor += 1;
    }
    ctx.restore();
  };
  shown.forEach((line, i) => {
    const size = fs[i];
    const cy = cur + size / 2;
    cur += size + gap;
    ctx.font = `700 ${Math.round(size)}px '${fontFamily}', sans-serif`;
    const chars = Array.from(String(line.text));
    const widths = chars.map((c) => ctx.measureText(c).width);
    const W = widths.reduce((a, b) => a + b, 0) || 1;
    if (shape === "arcoiris" || shape === "curva") {
      const up = shape === "arcoiris";
      const h = Math.max(4, (curv / 100) * (W * 0.5));
      const R = (W * W) / (8 * h) + h / 2;
      const ccY = up ? cy - h + R : cy + h - R;
      let acc = -W / (2 * R);
      chars.forEach((ch, k) => {
        const cw = widths[k];
        const phi = acc + cw / R / 2;
        const px = Math.sin(phi) * R;
        const py = up ? ccY - Math.cos(phi) * R : ccY + Math.cos(phi) * R;
        drawChar(ch, px, py, up ? phi : -phi);
        acc += cw / R;
      });
    } else {
      const ang = shape === "diagonal" ? -(curv / 100) * 0.52 : 0;
      ctx.save();
      ctx.translate(0, cy);
      if (ang) ctx.rotate(ang);
      let x = -W / 2;
      chars.forEach((ch, k) => {
        const cw = widths[k];
        drawChar(ch, x + cw / 2, 0, 0);
        x += cw;
      });
      ctx.restore();
    }
  });
};

export default drawShapedText;
